import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { OccurrenceSchema } from './lottery/occurrence';
import { SimpleSignupConfigSchema } from './simple-signup';
import { IntegrityCodesSchema } from './retail-unit';

export type RetailChannel = Static<typeof RetailChannelSchema>;
export const RetailChannelSchema = Type.Union(
  [Type.Literal('ALL'), Type.Literal('ONLINE'), Type.Literal('STORE'), Type.Literal('REMOTE')],
  {
    $id: 'RetailChannelSchema',
  }
);

export const DataSourcePurchaseSchema = Type.Literal('PURCHASE', {
  $id: 'DataSourcePurchaseSchema',
});

export const DataSourceSwipeSchema = Type.Literal('SWIPE', {
  $id: 'DataSourceSwipeSchema',
});

export const DataSourceSimpleSignupSchema = Type.Literal('SIMPLE_SIGNUP', {
  $id: 'DataSourceSimpleSignupSchema',
});

export type PurchaseDataSource = Static<typeof PurchaseDataSourceSchema>;
export const PurchaseDataSourceSchema = Type.Object(
  {
    dataSourceType: DataSourcePurchaseSchema,
    retailChannels: Type.Array(RetailChannelSchema, {
      uniqueItems: true,
      minItems: 1,
    }),
    minimumPurchaseAmount: Type.Optional(Type.Number({ minimum: 1 })),
  },
  { additionalProperties: false, $id: 'PurchaseDataSourceSchema' }
);

export type SwipeDataSource = Static<typeof SwipeDataSourceSchema>;
export const SwipeDataSourceSchema = Type.Object({
  dataSourceType: DataSourceSwipeSchema,
});

export type SimpleSignupDataSource = Static<typeof SimpleSignupDataSourceSchema>;
export const SimpleSignupDataSourceSchema = Type.Object({
  dataSourceType: DataSourceSimpleSignupSchema,
});

export type DataSource = Static<typeof DataSourceSchema>;
export const DataSourceSchema = Type.Union(
  [PurchaseDataSourceSchema, SwipeDataSourceSchema, SimpleSignupDataSourceSchema],
  {
    $id: 'DataSourceSchema',
  }
);

export type DataSourceType = Static<typeof DataSourceTypeSchema>;
export const DataSourceTypeSchema = Type.Union(
  [DataSourcePurchaseSchema, DataSourceSwipeSchema, DataSourceSimpleSignupSchema],
  {
    $id: 'DataSourceTypeSchema',
  }
);

export type LotteryType = Static<typeof LotteryTypeSchema>;
export const LotteryTypeSchema = Type.Union([Type.Literal('LOTTERY')], {
  $id: 'LotteryTypeSchema',
});

export type StoreIds = Static<typeof StoreIdsSchema>;
export const StoreIdsSchema = Type.Array(Type.String(), { $id: 'StoreIdsSchema', minItems: 1 });

export type MaxWinnersPerStore = Static<typeof MaxWinnersPerStoreSchema>;
export const MaxWinnersPerStoreSchema = Type.Object(
  {
    maxNbrWinnersPerStore: Type.Optional(Type.Integer({ minimum: 1, maximum: 20000 })),
    storeIds: StoreIdsSchema,
  },
  { additionalProperties: false, $id: 'MaxWinnersPerStoreSchema' }
);

export type EmailMarketingConstraint = Static<typeof EmailMarketingConstraintSchema>;
export const EmailMarketingConstraintSchema = Type.Object(
  {
    emailMarketingRequired: Type.Boolean(),
  },
  { additionalProperties: false, $id: 'EmailMarketingConstraintSchema' }
);

export type DrawConstraints = Static<typeof DrawConstraintsSchema>;
export const DrawConstraintsSchema = Type.Union(
  [MaxWinnersPerStoreSchema, EmailMarketingConstraintSchema],
  {
    $id: 'DrawConstraintsSchema',
  }
);

export type ApprovalStatus = Static<typeof ApprovalStatusSchema>;
export const ApprovalStatusSchema = Type.Union(
  [Type.Literal('APPROVED'), Type.Literal('WAITING'), Type.Literal('DENIED')],
  { $id: 'ApprovalStatusSchema' }
);

export type LotteryStatus = Static<typeof LotteryStatusSchema>;
export const LotteryStatusSchema = Type.Union(
  [
    Type.Literal('PENDING'),
    Type.Literal('ONGOING'),
    Type.Literal('COMPLETED'),
    Type.Literal('STOPPED'),
  ],
  { $id: 'LotteryStatusSchema' }
);

export type SwipeBaseConfigType = Static<typeof SwipeBaseConfigSchema>;
export const SwipeBaseConfigSchema = Type.Object(
  {
    timePerQuestion: Type.Integer({ minimum: 10, maximum: 20 }),
  },
  { additionalProperties: false, $id: 'SwipeBaseConfigSchema' }
);

export type SwipeNormalType = Static<typeof SwipeNormalSchema>;
export const SwipeNormalSchema = Type.Object(
  {
    maxNumberOfLives: Type.Integer({ minimum: 3, maximum: 5 }),
    numberOfCorrectAnswers: Type.Integer({ minimum: 3, maximum: 20 }),
    mode: Type.Literal('NORMAL'),
  },
  { additionalProperties: false, $id: 'SwipeNormalSchema' }
);

export type SwipePeacefulType = Static<typeof SwipePeacefulSchema>;
export const SwipePeacefulSchema = Type.Object(
  {
    amountOfQuestions: Type.Integer({ minimum: 3, maximum: 30 }),
    mode: Type.Literal('PEACEFUL'),
  },
  { additionalProperties: false, $id: 'SwipePeacefulSchema' }
);

export type SwipeConfigType = Static<typeof SwipeSchema>;
export const SwipeSchema = Type.Object(
  {
    config: Type.Object({
      timePerQuestion: SwipeBaseConfigSchema.properties.timePerQuestion,
      modeData: Type.Union([SwipeNormalSchema, SwipePeacefulSchema]),
    }),
  },
  { additionalProperties: false, $id: 'SwipeSchema' }
);

export type Modules = Static<typeof ModulesSchema>;
export const ModulesSchema = Type.Union([
  Type.Literal('claim-my-prize'),
  Type.Literal('email'),
  Type.Literal('general'),
  Type.Literal('max-winners-per-store'),
  Type.Literal('email-marketing-constraint'),
  Type.Literal('monthly-occurrence'),
  Type.Literal('purchase'),
  Type.Literal('simple-signup'),
  Type.Literal('single-occurrence'),
  Type.Literal('swipe'),
  Type.Literal('weekly-occurrence'),
  Type.Literal('winners'),
  Type.Literal('legal'),
  Type.Literal('email-subscription'),
  Type.Literal('no-communication'),
  Type.Literal('minimum-purchase-amount'),
]);

export type FamilyAccount = Static<typeof FamilyAccountSchema>;
const FamilyAccountSchema = Type.Object({
  subscriptions: Type.Object({
    emailSubscription: Type.Optional(
      Type.Object({
        emailMarketingEnabled: Type.Boolean(),
        emailMarketingRequired: Type.Boolean(),
      })
    ),
  }),
});

export type FamilySubscription = Static<typeof FamilySubscriptionSchema>;
const FamilySubscriptionSchema = Type.Object({
  subscriptionType: Type.Union([Type.Literal('email')]),
});

export type FamilySubscriptions = Static<typeof FamilySubscriptionsSchema>;
export const FamilySubscriptionsSchema = Type.Array(FamilySubscriptionSchema);

export type EmailTemplate = Static<typeof EmailTemplateSchema>;
const EmailTemplateSchema = Type.Object({
  bannerImageUrl: Type.String({ maxLength: 1000 }),
});

export type Lottery = Static<typeof LotterySchema>;
export const LotterySchema = Type.Object(
  {
    approvedBy: Type.Optional(Type.String()),
    approvalStatus: ApprovalStatusSchema,
    createdAt: Type.Optional(Type.String({ format: 'date-time' })),
    createdBy: Type.String(),
    dataSource: DataSourceSchema,
    description: Type.String({ minLength: 1, maxLength: 2000 }),
    drawConstraints: Type.Optional(Type.Array(DrawConstraintsSchema)),
    drawDay: Type.Integer(),
    drawTime: Type.String(),
    id: Type.String(),
    lotteryStatus: LotteryStatusSchema,
    lotteryTemplateId: Type.String(),
    lotteryType: LotteryTypeSchema,
    modules: Type.Array(ModulesSchema),
    name: Type.String({ minLength: 1, maxLength: 50 }),
    nbrDrawsMade: Type.Integer({ minimum: 0 }),
    nbrWinners: Type.Integer({ minimum: 1, maximum: 20000 }),
    nextDrawId: Type.String(),
    nextNextDrawId: Type.String(),
    occurrence: OccurrenceSchema,
    updatedAt: Type.Optional(Type.String({ format: 'date-time' })),
    updatedBy: Type.String(),
    cmp: Type.Optional(Type.Object({ days: Type.Number({ minimum: 1, maximum: 10 }) })),
    swipe: Type.Optional(SwipeSchema),
    simpleSignup: Type.Optional(SimpleSignupConfigSchema),
    integrityCodes: Type.Optional(Type.Array(IntegrityCodesSchema)),
    familyAccount: Type.Optional(FamilyAccountSchema),
    emailTemplate: Type.Optional(EmailTemplateSchema),
  },
  { additionalProperties: false, $id: 'LotterySchema' }
);

/**
 * LotteryConfig is used when creating/editing lotteries
 *
 * LotteryConfig must be a subset of Lottery, eg the following code must be valid:
 * ```
 * const lottery: Lottery = { /.../ };
 * const lotteryConfig: LotteryConfig = lottery; // Assign Lottery to LotteryConfig
 * ```
 */
export type LotteryConfig = Static<typeof LotteryConfigSchema>;
export const LotteryConfigSchema = Type.Object(
  {
    createdAt: Type.Optional(Type.String({ format: 'date-time' })),
    dataSource: DataSourceSchema,
    description: Type.String(),
    drawConstraints: Type.Optional(Type.Array(DrawConstraintsSchema)),
    drawDay: Type.Integer(),
    drawTime: Type.String(),
    lotteryTemplateId: Type.String(),
    modules: Type.Array(ModulesSchema),
    name: Type.String({ minLength: 1 }),
    nbrWinners: Type.Integer({ minimum: 1 }),
    occurrence: OccurrenceSchema,
    cmp: Type.Optional(
      Type.Object({
        days: Type.Number({ minimum: 1, maximum: 10 }),
      })
    ),
    swipe: Type.Optional(SwipeSchema),
    simpleSignup: Type.Optional(SimpleSignupConfigSchema),
    integrityCodes: Type.Optional(Type.Array(IntegrityCodesSchema)),
    familyAccount: Type.Optional(FamilyAccountSchema),
    emailTemplate: Type.Optional(
      Type.Object({
        bannerImageUrl: Type.String({ maxLength: 1000 }),
      })
    ),
  },
  { additionalProperties: false, $id: 'LotteryConfigSchema' }
);
